class DropdownMenu extends HTMLElement {
  constructor() {
    super();

    this.classes = {
      hover: 'is-hover',
      active: 'is-active',
      animate: 'show-from-bottom',
    };

    this.selectors = {
      header: '.shopify-section-header',
      background: '.header-background',
      topMenuItem: '.header__menu-item--top',
      menus: 'dropdown-menu, mega-menu',
      childAnimate: '.list-menu-dropdown > .list-menu'
    };

    this.openTimer = null;
    this.closeTimer = null;

    // Bind once
    this.onEnter = this.handleEnter.bind(this);
    this.onLeave = this.handleLeave.bind(this);
    this.onFocusIn = this.handleEnter.bind(this);

    this.addEventListener('mouseenter', this.onEnter);
    this.addEventListener('focusin', this.onFocusIn);
    this.addEventListener('mouseleave', this.onLeave);
  }

  connectedCallback() {
    this.header = this.header || document.querySelector(this.selectors.header);
  }

  disconnectedCallback() {
    clearTimeout(this.openTimer);
    clearTimeout(this.closeTimer);
  }

  handleEnter() {
    // If we were about to close, cancel that.
    clearTimeout(this.closeTimer);

    if (this.classList.contains(this.classes.active)) return;

    this.removeClasses();
    this.addClasses();
    this.resetBackground();
    this.animateMenu();

    if (this.header) this.header.classList.add(this.classes.hover);
  }

  handleLeave() {
    if (!this.header) return;

    // Close with a small delay to prevent flicker and allow slow mouse movement.
    clearTimeout(this.closeTimer);
    this.closeTimer = setTimeout(() => {
      // If user re-entered or focused within, do nothing.
      if (this.matches(':hover') || this.contains(document.activeElement)) return;

      this.reset();
      this.resetBackground();
      this.header.classList.remove(this.classes.hover);
    }, 120);
  }

  addClasses() {
    const isTopLevel = this.querySelector(this.selectors.topMenuItem);

    let delay = 0;
    if (isTopLevel && !this.matches(':hover')) {
      delay = 150;
      if (this.closest('.header--top-center') || this.closest('.header--top-left')) {
        delay = 200;
      }
    }

    clearTimeout(this.openTimer);
    this.openTimer = setTimeout(() => {
      // Only activate if still hovered/focused.
      if (this.matches(':hover') || this.contains(document.activeElement)) {
        this.classList.add(this.classes.active);
      }
    }, delay);

    if (this.header) this.header.classList.add(this.classes.hover);
  }

  removeClasses() {
    if (!this.header) return;

    const menus = this.header.querySelectorAll(this.selectors.menus);
    menus.forEach((menu) => {
      if (menu === this) return;
      // Reset other menus so only one stays open
      if (typeof menu.reset === 'function') menu.reset();
    });
  }

  reset() {
    this.classList.remove(this.classes.active);

    const childAnimate = this.querySelector(this.selectors.childAnimate);
    if (childAnimate) childAnimate.classList.remove(this.classes.animate);

    clearTimeout(this.openTimer);
  }

  resetBackground() {
    if (!this.header) return;
    const background = this.header.querySelector(this.selectors.background);
    if (background) background.classList.remove(this.classes.active);
  }

  animateMenu() {
    const childAnimate = this.querySelector(this.selectors.childAnimate);
    if (!childAnimate) return;

    // Avoid stacking multiple timeouts if user flicks across menus
    childAnimate.classList.remove(this.classes.animate);

    setTimeout(() => {
      if (this.classList.contains(this.classes.active) || this.matches(':hover')) {
        childAnimate.classList.add(this.classes.animate);
      }
    }, 150);
  }

  close() {
    this.reset();
    this.resetBackground();
    if (this.header) this.header.classList.remove(this.classes.hover);
  }
}
customElements.define('dropdown-menu', DropdownMenu);


class MegaMenu extends HTMLElement {
  constructor() {
    super();

    this.classes = {
      hover: 'is-hover',
      active: 'is-active',
      animate: 'show-from-bottom',
    };

    this.selectors = {
      header: '.shopify-section-header',
      background: '.header-background',
      dropdown: '.list-menu-dropdown',
      menus: 'dropdown-menu, mega-menu',
      childAnimate: '.mega-menu__item, .mega-menu__promo'
    };

    this.timeouts = [];
    this.closeTimer = null;

    this.onEnter = this.handleEnter.bind(this);
    this.onLeave = this.handleLeave.bind(this);

    this.addEventListener('mouseenter', this.onEnter);
    this.addEventListener('focusin', this.onEnter);
    this.addEventListener('mouseleave', this.onLeave);
  }

  connectedCallback() {
    this.header = this.header || document.querySelector(this.selectors.header);
  }

  disconnectedCallback() {
    this.timeouts.forEach((t) => clearTimeout(t));
    clearTimeout(this.closeTimer);
  }

  handleEnter() {
    clearTimeout(this.closeTimer);

    if (this.classList.contains(this.classes.active)) return;

    this.removeClasses();
    this.addClasses();
    this.showSublist();
  }

  handleLeave() {
    if (!this.header) return;

    clearTimeout(this.closeTimer);
    this.closeTimer = setTimeout(() => {
      if (this.matches(':hover') || this.contains(document.activeElement)) return;

      this.reset();
      this.resetBackground();
      this.header.classList.remove(this.classes.hover);
    }, 150);
  }

  showSublist() {
    if (!this.header) return;

    const dropdown = this.querySelector(this.selectors.dropdown);
    const background = this.header.querySelector(this.selectors.background);
    if (!dropdown || !background) return;

    background.style.setProperty('--height', this.header.clientHeight + dropdown.clientHeight + 'px');
    background.classList.add(this.classes.active);
  }

  addClasses() {
    this.classList.add(this.classes.active);

    const childAnimate = this.querySelectorAll(this.selectors.childAnimate);
    const delay = childAnimate.length > 5 ? 75 : 150;

    // Clear any previous
    this.timeouts.forEach((t) => clearTimeout(t));
    this.timeouts = [];

    childAnimate.forEach((element, index) => {
      const t = setTimeout(() => {
        element.classList.add(this.classes.animate);
      }, (delay * index) + 100);
      this.timeouts.push(t);
    });

    if (this.header) this.header.classList.add(this.classes.hover);
  }

  removeClasses() {
    if (!this.header) return;

    const menus = this.header.querySelectorAll(this.selectors.menus);
    menus.forEach((menu) => {
      if (menu === this) return;
      if (typeof menu.reset === 'function') menu.reset();
    });
  }

  reset() {
    this.classList.remove(this.classes.active);

    const childAnimate = this.querySelectorAll(this.selectors.childAnimate);
    childAnimate.forEach((el) => el.classList.remove(this.classes.animate));

    this.timeouts.forEach((t) => clearTimeout(t));
    this.timeouts = [];
  }

  resetBackground() {
    if (!this.header) return;
    const background = this.header.querySelector(this.selectors.background);
    if (background) background.classList.remove(this.classes.active);
  }

  close() {
    this.reset();
    this.resetBackground();
    if (this.header) this.header.classList.remove(this.classes.hover);
  }
}
customElements.define('mega-menu', MegaMenu);